import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useQueryClient } from "@tanstack/react-query";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import Dialog from "components/DesignComponents/Dialog";
import { BannerData, DialogHandler, QUERY_KEYS, useApi } from "shared";

import Banner from "./Banner";
import { useBannersList, useBannerUpdate } from "./hooks";
import Loader from "./Loader";

const BannerManagement = ({ isOpen, close }: DialogHandler) => {
    const { t } = useTranslation();
    const { handleResponse } = useApi();
    const queryClient = useQueryClient();

    const { data: banners = [], isLoading: isInitialLoading } =
        useBannersList(isOpen);

    const { mutate: handleUpdate, isPending: isSubmitting } = useBannerUpdate(
        response => {
            close();
            handleResponse(response);
        }
    );

    const [payload, setPayload] = useState<BannerData | null>(null);

    const handlePayload = (data: BannerData) => setPayload(data);

    const handleSubmit = () => payload && handleUpdate(payload);

    return (
        <Dialog
            title={t("General##banner management")}
            open={isOpen}
            close={close}
            submit={handleSubmit}
            isTitleSeparator
            isActionsSeparator
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isSubmitting}
                    isReadyToConfirm={payload !== null}
                    secondaryButtonText={t("Button##close")}
                />
            }
            extendToMaxWidth
            slotProps={{
                transition: {
                    onExited: () => {
                        setPayload(null);

                        queryClient.resetQueries({
                            queryKey: QUERY_KEYS.banners.list
                        });
                    }
                }
            }}
        >
            {isInitialLoading ? (
                <Loader />
            ) : (
                banners.map(banner => (
                    <Banner
                        key={banner.id}
                        data={banner}
                        isLoading={isSubmitting}
                        onSubmit={handlePayload}
                    />
                ))
            )}
        </Dialog>
    );
};

export default BannerManagement;
